/**
 * Secret scrubbing for anything a runner attempt persists.
 *
 * @module types/secret-redaction
 * @description Lifted verbatim out of `apps/api/src/external-tests/artifact-harvest.ts`
 * (which now re-exports it) because redaction has to happen in **two** places
 * once a runner provisions itself:
 *
 *  - on the worker, over files harvested out of a bind-mounted workspace, and
 *  - **inside the container**, over the output tree, before it is tarred and
 *    PUT to S3 — because under Fargate the worker never sees the raw bytes.
 *
 * One implementation, one set of tests, no possibility of the container being
 * laxer than the harvester.
 */

/** What a scrubbed secret is replaced with. */
export const REDACTION_PLACEHOLDER = '[REDACTED]';

/**
 * Shortest secret we will scrub. A 1–3 character value carries almost no
 * entropy, while rewriting every occurrence of, say, `1` would shred the log
 * we're keeping in order to debug with. Real credentials are far longer.
 */
export const MIN_REDACTABLE_SECRET_LENGTH = 4;

/** The scrubbed text plus how much it had to scrub. */
export interface RedactionResult {
  text: string;
  /** Number of substitutions made, summed over every secret. */
  hits: number;
}

/**
 * Scrub materialized secret values out of text destined for storage, reporting
 * how many substitutions it made.
 *
 * Replaces longest-first so that when one secret contains another (a password
 * that is a prefix of a token, say) the outer match wins and can't leave a
 * readable fragment behind.
 *
 * **Why the count exists.** This function runs TWICE on the same bytes for a
 * self-provisioning runner: once in-container (`runner-entrypoint/pack.ts`) and
 * once again control-plane-side (`external-tests/artifact-harvest.ts`). The
 * second pass is versioned with the API; the first ships inside a container
 * image that can be months stale. If the two ever drift — a new `$var.*` source
 * the old image does not know to scrub, a changed minimum length — the *only*
 * signal available today is a customer finding a live credential in an artifact.
 *
 * A non-zero count on the second pass is that signal, months earlier: it means
 * bytes reached us that the in-container pass should already have scrubbed. The
 * caller decides what to do with it (see `artifact-harvest.ts`, which warns);
 * this module only has to make it observable.
 *
 * @remarks Substring matching only. A secret that the suite re-encodes before
 * printing — base64, URL-escaped, JSON-escaped — will not match. This narrows
 * the blast radius rather than eliminating it; the real containment is that
 * `envMapping` is an allowlist that should only ever carry least-privilege test
 * personas (see the env-materializer module docs).
 */
export function redactSecretsWithCount(
  text: string,
  secretValues: readonly string[]
): RedactionResult {
  const targets = [...new Set(secretValues)]
    .filter((v) => v.length >= MIN_REDACTABLE_SECRET_LENGTH)
    .sort((a, b) => b.length - a.length);

  let out = text;
  let hits = 0;
  for (const secret of targets) {
    const parts = out.split(secret);
    // `split` yields one more part than there were occurrences, and `join` is
    // then a no-op when there were none — so this stays a single pass per
    // secret rather than a separate count-then-replace.
    hits += parts.length - 1;
    out = parts.join(REDACTION_PLACEHOLDER);
  }
  return { text: out, hits };
}

/**
 * Scrub materialized secret values out of text destined for storage.
 *
 * The ergonomic form — a string in, a string out — for the callers that only
 * need the bytes. {@link redactSecretsWithCount} is the same pass with the
 * drift signal attached.
 */
export function redactSecrets(text: string, secretValues: readonly string[]): string {
  return redactSecretsWithCount(text, secretValues).text;
}

/** Extensions worth a redaction pass. Binaries (video, trace zips) are skipped. */
export const REDACTABLE_TEXT_EXTENSIONS = ['.jsonl', '.json', '.html', '.log', '.txt'] as const;

/**
 * Text artifacts get a redaction pass; binaries do not.
 *
 * A substring scrub only works on text, and it is only worth the re-encode for
 * files that plausibly echo a credential — a login POST body in `network.jsonl`,
 * an app that logs its own auth payload to the console.
 */
export function isRedactableTextPath(relPath: string): boolean {
  const lowered = relPath.toLowerCase();
  return REDACTABLE_TEXT_EXTENSIONS.some((ext) => lowered.endsWith(ext));
}

/** Skip any single file larger than this. */
export const MAX_ARTIFACT_BYTES = 200 * 1024 * 1024;

/** A separate ceiling for long session recordings; other artifacts stay at 200 MiB. */
export const MAX_VIDEO_ARTIFACT_BYTES = 384 * 1024 * 1024;

/**
 * Largest text artifact we will attempt to REDACT.
 *
 * Redaction is not streaming and cannot cheaply be made so: the whole file is
 * read into a JS string and {@link redactSecretsWithCount} does one
 * `split`/`join` per secret, each allocating a fresh copy. In UTF-16 that is
 * ~2× the file on disk, *per secret*, all of it live at once — a 200 MB
 * `network.jsonl` with a handful of `$var.*` values is comfortably over a
 * gigabyte of transient allocation inside a Fargate task whose entire memory
 * budget is smaller than that. The container OOMs, PID 1 dies, and the attempt
 * reports nothing at all: the cure is worse than the artifact.
 *
 * So there is a second, much lower cap for text that has to be scrubbed. It is
 * NOT the per-file cap: a 100 MB video is copied byte-for-byte and never
 * touched by the redactor, and capping it here would throw away evidence for no
 * reason.
 *
 * **A file over this cap is DROPPED, never uploaded raw.** That direction is
 * the whole point — the file is over the cap precisely because it is a huge
 * text file produced by the customer's suite, which is exactly the shape of
 * thing most likely to contain a credential. Shipping it unscrubbed to trade a
 * memory risk for a disclosure risk would be the wrong trade in the only case
 * that matters. 32 MB is roughly 300k log lines: far beyond what anyone reads,
 * and still only ~130 MB of transient allocation at four secrets.
 */
export const MAX_REDACTABLE_TEXT_BYTES = 32 * 1024 * 1024;
/** Stop collecting once an attempt's artifacts exceed this in total. */
export const MAX_ATTEMPT_BYTES = 500 * 1024 * 1024;
/** Upper bound on files collected from one attempt. */
